/**
 * DestructiveConfirmModal — May 2026 safety hardening.
 *
 * One modal to gate every UI destructive action. Pattern (mirrors
 * `safety.py` on the backend):
 *
 *   1. Big red banner — "this is irreversible".
 *   2. Show the impact preview (count of rows affected, etc.) the
 *      caller passes in.
 *   3. Operator must type the exact `requiredText` string. We don't
 *      use a checkbox here — typing forces a 2-3 second cognitive
 *      pause that has been empirically shown to prevent fat-finger
 *      wipes.
 *   4. (Optional) operator password re-entry, gated by `requireAdminToken`.
 *   5. Confirm button is disabled until everything matches.
 *
 * Usage:
 *   <DestructiveConfirmModal
 *     open={showWipe}
 *     onClose={() => setShowWipe(false)}
 *     onConfirm={async ({ confirmText, adminToken }) => {
 *       await api.post('/transactions/reset-all', { confirm: confirmText, admin_token: adminToken });
 *     }}
 *     title="Wipe ALL transactions"
 *     impactSummary="This will delete 12,345 transaction rows."
 *     requiredText="I UNDERSTAND THIS IS IRREVERSIBLE"
 *     requireAdminToken={false}
 *   />
 */
import React, { useState, useEffect } from "react";
import { Warning, XCircle } from "@phosphor-icons/react";

export default function DestructiveConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  impactSummary,
  requiredText = "I UNDERSTAND THIS IS IRREVERSIBLE",
  requireAdminToken = false,
  confirmButtonLabel = "Permanently delete",
}) {
  const [typed, setTyped] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) {
      setTyped("");
      setAdminToken("");
      setBusy(false);
      setErr("");
    }
  }, [open]);

  if (!open) return null;

  const textMatches = typed === requiredText;
  const tokenOk = !requireAdminToken || adminToken.length > 0;
  const canConfirm = textMatches && tokenOk && !busy;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setErr("");
    try {
      await onConfirm({ confirmText: typed, adminToken });
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Action failed");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="destructive-confirm-overlay"
    >
      <div
        className="bg-white w-full max-w-lg border-2 border-red-500"
        onClick={(e) => e.stopPropagation()}
        data-testid="destructive-confirm-modal"
      >
        <div className="bg-red-50 border-b border-red-300 p-4 flex items-start gap-3">
          <Warning size={28} className="text-red-700 mt-1 flex-shrink-0"/>
          <div>
            <h3 className="text-red-900 font-bold text-lg leading-tight">{title}</h3>
            <p className="text-red-700 text-xs mt-1 leading-snug">
              This action is <b>irreversible</b>. Deleted rows cannot be undone except by restoring a Mongo backup
              (see <code>/app/MONGO_BACKUP.md</code>).
            </p>
          </div>
        </div>

        <div className="p-4 space-y-4 text-sm">
          {impactSummary && (
            <div className="border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900" data-testid="destructive-impact-summary">
              {impactSummary}
            </div>
          )}

          <div>
            <div className="overline mb-1">Type exactly to confirm</div>
            <div className="font-mono text-xs text-zinc-500 mb-1 select-all" data-testid="destructive-required-text">
              {requiredText}
            </div>
            <input
              className="gf-input font-mono"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type the exact phrase above"
              autoFocus
              data-testid="destructive-confirm-input"
            />
          </div>

          {requireAdminToken && (
            <div>
              <div className="overline mb-1">Admin password / token</div>
              <input
                type="password"
                className="gf-input"
                value={adminToken}
                onChange={(e) => setAdminToken(e.target.value)}
                placeholder="Re-enter to authorise"
                data-testid="destructive-admin-token-input"
              />
            </div>
          )}

          {err && (
            <div className="border border-red-300 bg-red-50 p-2 text-xs text-red-900 flex items-start gap-2" data-testid="destructive-error">
              <XCircle size={14} className="mt-0.5 flex-shrink-0"/>
              <span>{err}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button
            className="gf-btn gf-btn-outline"
            onClick={onClose}
            disabled={busy}
            data-testid="destructive-cancel"
          >
            Cancel
          </button>
          <button
            className="gf-btn gf-btn-danger"
            style={{ background: canConfirm ? "#dc2626" : "#fca5a5", color: "white" }}
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="destructive-confirm-button"
          >
            {busy ? "Working…" : confirmButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
